/**
 * Shared layout logic used by both `default.vue` and `authenticated.vue` layouts.
 *
 * Handles:
 *  - Window resize tracking → `isMobile` ref + `uiStore.windowSize`
 *  - "Go to maker" cart navigation
 *  - Overflow locking when the cart overlay is open
 *  - Closing the mobile cart overlay (and search) on route change, so a
 *    post-checkout redirect does not land on /orders/upcoming with an empty
 *    basket still open. Lived only on `default.vue` before; authenticated
 *    layouts (group-select / edit → upcoming) never closed it.
 */
export function useLayoutSetup() {
  const route = useRoute();
  const uiStore = useUiStore();
  const cartStore = useOrdersStore();

  const isMobile = ref(false);

  function handleResize() {
    uiStore.setWindowSize(window.innerWidth);
    isMobile.value = window.innerWidth < 768;
  }

  function goToMaker(val: boolean) {
    if (cartStore.cartItemCount > 0 && val) {
      if (route.name === "store-slug") {
        window.scrollTo({ top: 600, behavior: "smooth" });
      } else if (cartStore.cart?.maker?.slug) {
        navigateTo(`/store/${cartStore.cart.maker.slug}`);
      }
    }
  }

  // Lock body scroll when mobile cart is open
  watch(
    () => cartStore.showCart,
    (show) => {
      if (import.meta.client) {
        document.documentElement.style.overflowY = show ? "hidden" : "scroll";
      }
    },
  );

  // Skip the initial run (`oldName` is undefined) so a layout remount on the
  // current page does not yank the overlay closed. Subsequent name changes
  // always dismiss it — same behaviour the consumer layout already had.
  watch(
    () => route.name,
    (_newName, oldName) => {
      if (oldName) {
        cartStore.setShowCart(false);
        uiStore.setSearchStatus(false);
      }
    },
  );

  onMounted(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
  });

  onUnmounted(() => {
    window.removeEventListener("resize", handleResize);
  });

  return { isMobile, goToMaker };
}
